<div class="container-fluid">
  <!-- Header -->
  <div class="row mb-4">
    <div class="col">
      <h3>User Migration</h3>
      <p class="text-muted">
        Import users from a JSON file. Users will be created with randomly
        generated passwords.
      </p>
      <div class="alert alert-info" role="alert">
        <i class="fa fa-info-circle"></i>
        <strong>Note:</strong> Users are system-wide but role assignments are
        jurisdiction-specific. Select a jurisdiction to assign users to roles
        within that jurisdiction. Roles specified in the migration file will only
        be assigned if they exist in the selected jurisdiction.
      </div>
    </div>
  </div>

  <!-- Error Message -->
  <div *ngIf="error" class="row mb-4">
    <div class="col">
      <div class="alert alert-danger alert-dismissible fade show" role="alert">
        <i class="fa fa-exclamation-triangle"></i>
        <strong>Error:</strong> {{ error }}
        <button
          type="button"
          class="close"
          data-dismiss="alert"
          aria-label="Close"
          (click)="error = null"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </div>
  </div>

  <ul class="nav nav-tabs mb-4" role="tablist">
    <li class="nav-item">
      <button
        type="button"
        class="nav-link"
        [class.active]="activeTab === 'new'"
        (click)="selectTab('new')"
      >
        New Migration
      </button>
    </li>
    <li class="nav-item">
      <button
        type="button"
        class="nav-link"
        [class.active]="activeTab === 'batches'"
        (click)="selectTab('batches')"
      >
        Migration Batches
      </button>
    </li>
  </ul>

  <!-- Migration Form -->
  <div *ngIf="activeTab === 'new' && !showCredentials" class="row">
    <div class="col-lg-8">
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">User Migration Details</h5>
        </div>
        <div class="card-body">
          <app-loader *ngIf="loading"></app-loader>

          <form *ngIf="!loading" (ngSubmit)="migrateUsers()">
            <!-- Jurisdiction Selector -->
            <div class="form-group">
              <label class="form-label">
                <strong>Jurisdiction</strong>
                <span class="text-danger">*</span>
              </label>
              <select
                class="form-control"
                [(ngModel)]="selectedClientId"
                name="clientId"
                required
              >
                <option [ngValue]="null">-- Select Jurisdiction --</option>
                <option *ngFor="let client of clients" [value]="client.id">
                  {{ client.name }}
                </option>
              </select>
              <small class="form-text text-muted">
                Select the jurisdiction where roles should be assigned. Users will
                only be assigned to roles that belong to this jurisdiction.
              </small>
            </div>

            <!-- User Migration File Upload -->
            <div class="form-group">
              <label class="form-label">
                <strong>User Migration File</strong>
                <span class="text-danger">*</span>
              </label>
              <wm-upload
                #userMigrationUploadComponent
                buttonText="Select User Migration File"
                validDocumentTypes="json"
                [pathSegments]="['user-migration', uploadKey]"
                (fileUploaded)="onUserMigrationFileUploaded($event)"
                (fileDeleted)="onUserMigrationFileDeleted($event)"
                [key]="uploadKey + '-users'"
                [allowMultipleFiles]="false"
                category="UserMigration"
                [deleteStoredDocument]="false"
                [showUploadedFilesList]="true"
              ></wm-upload>
              <small class="form-text text-muted">
                <strong>User Migration File (JSON):</strong> Contains user data
                including username, name, email, phone numbers, and address
                information.
              </small>
              <div class="mt-2">
                <a
                  href="assets/sample-user-migration.json"
                  download="sample-user-migration.json"
                  class="btn btn-sm btn-outline-info"
                  target="_blank"
                >
                  <i class="fa fa-download me-2"></i>
                  Download Sample User Migration File
                </a>
                <small class="form-text text-muted ml-2 d-inline">
                  Example with user data including addresses
                </small>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="form-group mt-4">
              <button
                type="submit"
                class="btn btn-primary mr-2"
                [disabled]="
                  loading ||
                  migrationInProgress ||
                  !userMigrationUploaded ||
                  !selectedClientId
                "
              >
                <i class="fa fa-upload"></i>
                <span *ngIf="!migrationInProgress">Migrate Users</span>
                <span *ngIf="migrationInProgress">
                  <i class="fa fa-spinner fa-spin"></i> Migrating...
                </span>
              </button>
              <button
                type="button"
                class="btn btn-secondary"
                (click)="cancel()"
                [disabled]="loading || migrationInProgress"
              >
                <i class="fa fa-times"></i>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Help Panel -->
    <div class="col-lg-4">
      <div class="card mb-3">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fa fa-info-circle"></i>
            Migration Process
          </h5>
        </div>
        <div class="card-body">
          <ol class="pl-3">
            <li class="mb-2">
              Upload a JSON file containing user data
            </li>
            <li class="mb-2">Click "Migrate Users" to start the import</li>
            <li class="mb-2">
              Assign users to roles for the appropriate jurisdiction
            </li>
            <li class="mb-2">
              Users will be forced to change their password on first login
            </li>
          </ol>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="fa fa-shield-alt"></i>
            Password Security
          </h5>
        </div>
        <div class="card-body">
          <p class="mb-2">
            <strong>Generated passwords are:</strong>
          </p>
          <ul class="pl-3">
            <li>16 characters long</li>
            <li>Include uppercase and lowercase letters</li>
            <li>Include numbers and special characters</li>
            <li>Randomly generated for each user</li>
          </ul>
          <p class="mt-3 mb-0 text-muted small">
            Users must change their password on first login for security.
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Migration Success Display -->
  <div *ngIf="activeTab === 'new' && showCredentials" class="row">
    <div class="col-12">
      <div class="card">
        <div class="card-header bg-success text-white">
          <h5 class="mb-0">
            <i class="fa" [ngClass]="{
              'fa-clock': migrationStatus === 'Queued',
              'fa-spinner fa-spin': migrationStatus === 'Processing',
              'fa-check-circle': migrationStatus === 'Completed',
              'fa-exclamation-triangle': migrationStatus === 'Error'
            }"></i>
            User Migration Status
          </h5>
        </div>
        <div class="card-body">
          <div
            class="alert"
            [ngClass]="{
              'alert-info': migrationStatus === 'Queued' || migrationStatus === 'Processing',
              'alert-success': migrationStatus === 'Completed',
              'alert-danger': migrationStatus === 'Error'
            }"
            role="alert"
          >
            <strong>{{ migrationStatus || 'Queued' }}</strong>
            <span *ngIf="migrationStatus === 'Queued'"> The migration is waiting for a worker.</span>
            <span *ngIf="migrationStatus === 'Processing'"> The migration is running.</span>
            <span *ngIf="migrationStatus === 'Completed'"> The migration completed successfully.</span>
            <span *ngIf="migrationStatus === 'Error'"> The migration did not complete.</span>
          </div>

          <div *ngIf="migrationTaskError" class="alert alert-danger" role="alert">
            {{ migrationTaskError }}
          </div>

          <div *ngIf="usernameConflictCount > 0" class="alert alert-warning" role="alert">
            <i class="fa fa-exclamation-triangle"></i>
            <strong>{{ usernameConflictCount }} username conflict(s)</strong> were renamed during migration.
            <button
              type="button"
              class="btn btn-link p-0 ml-2"
              (click)="downloadUsernameConflicts()"
            >
              Download conflict CSV
            </button>
          </div>

          <div class="mb-3">
            <button
              type="button"
              class="btn btn-primary"
              (click)="reset()"
            >
              <i class="fa fa-redo"></i>
              Migrate More Users
            </button>
          </div>

          <div class="alert alert-info mt-3" role="alert">
            <i class="fa fa-info-circle"></i>
            <strong>Next Steps:</strong>
            <ol class="mb-0 mt-2 pl-3">
              <li>
                Navigate to Role Management to assign users to appropriate roles
              </li>
              <li>
                Users will be required to change their password on first login
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div *ngIf="activeTab === 'batches'" class="row">
    <div class="col-12">
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between">
          <h5 class="mb-0">Migration Batches</h5>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            (click)="loadMigrationBatches()"
            [disabled]="loadingBatches"
          >
            <i class="fa fa-sync" [class.fa-spin]="loadingBatches"></i>
          </button>
        </div>
        <div class="card-body p-0">
          <app-loader *ngIf="loadingBatches"></app-loader>

          <div *ngIf="!loadingBatches && migrationBatches.length === 0" class="p-4 text-muted">
            No user migration batches have been created.
          </div>

          <div *ngIf="!loadingBatches && migrationBatches.length > 0" class="table-responsive">
            <table class="table table-hover mb-0 batch-table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Performed By</th>
                  <th>Jurisdiction</th>
                  <th>Source File</th>
                  <th>Status</th>
                  <th>Conflicts</th>
                  <th class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let batch of migrationBatches">
                  <td>{{ batch.migratedOn | date:'short' }}</td>
                  <td>{{ batch.migratedBy }}</td>
                  <td>{{ batch.jurisdiction || '—' }}</td>
                  <td class="text-truncate batch-file-path" [title]="batch.userMigrationFilePath">
                    {{ batch.userMigrationFilePath }}
                  </td>
                  <td>
                    <span
                      class="badge"
                      [ngClass]="{
                        'badge-info': batch.status === 'Queued',
                        'badge-primary': batch.status === 'Processing',
                        'badge-success': batch.status === 'Completed',
                        'badge-danger': batch.status === 'Error'
                      }"
                    >
                      {{ batch.status }}
                    </span>
                    <div *ngIf="batch.errorMessage" class="small text-danger mt-1">
                      {{ batch.errorMessage }}
                    </div>
                  </td>
                  <td>{{ batch.usernameConflictCount }}</td>
                  <td class="text-right">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary mr-1"
                      (click)="downloadMigrationFile(batch)"
                      title="Download the JSON file used for this import"
                    >
                      <i class="fa fa-download mr-1"></i> JSON
                    </button>
                    <button
                      *ngIf="batch.usernameConflictCount > 0"
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      (click)="downloadUsernameConflicts(batch.batchId)"
                      title="Download username conflict CSV"
                    >
                      <i class="fa fa-download mr-1"></i> CSV
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
